import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as action from '../../../Action/BulkSeriesImport/BulkSeriesImport_Action';
import { BATCH_STATUS, NOT_STARTED_BATCH_STATUSES, StatusBadge } from './status';
import { PAGE_SIZE, errorMessage, formatDateTime, num } from './helpers';

// Filter chips → the backend's comma-separated ?status= list.
const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'running', label: 'Running', status: 'VALIDATING,QUEUED,RUNNING' },
    { key: 'attention', label: 'Needs attention', status: 'COMPLETED_WITH_ERRORS,INVALID' },
    { key: 'not_started', label: 'Not started', status: 'DRAFT,READY,READY_WITH_WARNINGS' },
    { key: 'completed', label: 'Completed', status: 'COMPLETED' },
    { key: 'cancelled', label: 'Cancelled', status: 'CANCELLED' },
];

const COLUMNS = '72px minmax(0,1.5fr) minmax(0,1.1fr) 190px minmax(0,1.6fr) 130px 130px';

const Progress = ({ batch }) => {
    if (NOT_STARTED_BATCH_STATUSES.includes(batch.status)) {
        const toImport = num(batch.episode_count) - num(batch.episodes_ignored);
        return (
            <span className="bsi-muted" style={{ fontSize: 13 }}>
                {batch.status === 'INVALID' ? 'Nothing importable found' : `Not started · ${toImport} episodes ready`}
            </span>
        );
    }
    const total = num(batch.episode_count) || 1;
    const pct = (v) => `${(num(v) / total) * 100}%`;
    const done = num(batch.episodes_complete) + num(batch.episodes_ignored);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="bsi-bar" role="img" aria-label={`${done} of ${batch.episode_count} episodes done`}>
                <div style={{ width: pct(batch.episodes_complete), background: '#35ad65' }} />
                <div style={{ width: pct(batch.episodes_ignored), background: 'rgba(255,255,255,.3)' }} />
                <div style={{ width: pct(batch.episodes_in_progress), background: 'rgba(0,168,243,.55)' }} />
                <div style={{ width: pct(batch.episodes_failed), background: '#f74032' }} />
                <div style={{ width: pct(batch.episodes_cancelled), background: 'rgba(255,255,255,.15)' }} />
            </div>
            <span className="bsi-muted" style={{ fontSize: 12 }}>
                {done} of {num(batch.episode_count)} done
                {num(batch.episodes_in_progress) > 0 && ` · ${batch.episodes_in_progress} transcoding`}
                {num(batch.episodes_failed) > 0 && <span style={{ color: '#ff7a6e' }}> · {batch.episodes_failed} failed</span>}
                {num(batch.episodes_cancelled) > 0 && ` · ${batch.episodes_cancelled} cancelled`}
            </span>
        </div>
    );
};

const BatchList = ({ canManage, onOpen, onNew }) => {
    const dispatch = useDispatch();
    const [filter, setFilter] = useState('all');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const load = useCallback((nextPage) => {
        setLoading(true);
        const status = FILTERS.find((f) => f.key === filter)?.status;
        return dispatch(action.getBulkImportBatches({ page: nextPage, per_page: PAGE_SIZE, ...(status && { status }) }))
            .then((res) => {
                const list = res?.responseDetails || [];
                setRows(Array.from(list));
                setTotal(list.totalRecords ?? list.length);
                setPage(nextPage);
            })
            .catch((err) => toast.error(errorMessage(err, 'Failed to load batches')))
            .finally(() => setLoading(false));
    }, [dispatch, filter]);

    useEffect(() => { load(1); }, [load]);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="bsi-crumb">Content / Bulk Series Import</div>
                    <h1 className="bsi-h1">Bulk Series Import</h1>
                    <div className="bsi-muted" style={{ fontSize: 14 }}>
                        Import whole series, or extra episodes for an existing series, from the approved S3 location.
                    </div>
                </div>
                {canManage && (
                    <button type="button" className="bsi-btn primary" onClick={onNew}>
                        <i className="fas fa-plus" aria-hidden="true" /> New import
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div role="group" aria-label="Filter by status" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            className={`bsi-chip ${filter === f.key ? 'on' : ''}`}
                            aria-pressed={filter === f.key}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                {/* No auto-refresh — the admin refreshes to see the latest transcoding status. */}
                <button type="button" className={`bsi-btn ${rows.some((row) => !NOT_STARTED_BATCH_STATUSES.includes(row.status)) ? 'success' : 'ghost'} sm`} disabled={loading} aria-busy={loading} onClick={() => load(page)}>
                    <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} aria-hidden="true" /> {loading ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            <div className="bsi-card" style={{ overflow: 'hidden' }}>
                <div className="bsi-row head" style={{ gridTemplateColumns: COLUMNS }}>
                    <span className="bsi-th">Batch</span><span className="bsi-th">Label</span><span className="bsi-th">Type</span>
                    <span className="bsi-th">Status</span><span className="bsi-th">Progress</span>
                    <span className="bsi-th">Started by</span><span className="bsi-th">Created</span>
                </div>
                {loading && <div className="bsi-row bsi-muted">Loading…</div>}
                {!loading && rows.length === 0 && (
                    <div className="bsi-row bsi-muted">{filter === 'all' ? 'No imports yet.' : 'No batches match this filter.'}</div>
                )}
                {!loading && rows.map((b) => (
                    <div key={b.id} className="bsi-row clickable" style={{ gridTemplateColumns: COLUMNS }} onClick={() => onOpen(b.id)}>
                        <span className="bsi-mono" style={{ color: '#fff' }}>#{b.id}</span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <button type="button" className="bsi-link-btn bsi-title" style={{ color: '#fff' }} onClick={(e) => { e.stopPropagation(); onOpen(b.id); }}>
                                {b.label || `Batch #${b.id}`}
                            </button>
                            <span className="bsi-muted" style={{ fontSize: 12 }}>{num(b.series_count)} series</span>
                        </span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <span>{b.mode === 'ADD_EPISODES' ? 'Add episodes' : 'New series'}</span>
                            {b.mode === 'ADD_EPISODES' && b.target_series_title && (
                                <span className="bsi-muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    into {b.target_series_title}
                                </span>
                            )}
                        </span>
                        <span><StatusBadge map={BATCH_STATUS} status={b.status} /></span>
                        <Progress batch={b} />
                        <span>{b.requested_by_name || '—'}</span>
                        <span className="bsi-muted" style={{ fontSize: 13 }}>{formatDateTime(b.created_at)}</span>
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="bsi-muted" style={{ fontSize: 13 }}>
                    {total > 0 && `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} batches`}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="bsi-btn ghost sm" disabled={page <= 1} onClick={() => load(page - 1)}>Previous</button>
                    <button type="button" className="bsi-btn ghost sm" disabled={page * PAGE_SIZE >= total} onClick={() => load(page + 1)}>Next</button>
                </div>
            </div>
        </>
    );
};

export default BatchList;
